import api from '@/api'
import vuex from '@/store'
import store from 'store'
import cookies from 'js-cookie'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import router from '@/router'
import _ from 'lodash'
import { OpenFeature } from '@openfeature/web-sdk'

dayjs.extend(relativeTime)

export default {
  state: {
    list: [],
    groups: [],
    groupMemberships: [],
    current: store.get('ews_current_station', {}),
    settings: {},
    stagehandHealth: {},
    preferences: {},
    notificationEnabledStationsListData: [],
    announcements: [],
    announcementsArchive: []
  },
  mutations: {
    LOAD_STATIONS(state, list) {
      state.list = list.map((station) => ({
        ...station,
        label: `${station.station_name.toUpperCase()}${station.station_location?.length > 0 ? ` - ${station.station_location}` : ''}`,
        value: station.station_name
      }))
    },
    SET_STATION(state, station) {
      state.current = station
      store.set('ews_current_station', station)
    },
    SET_STATION_PREFERENCES(state, preferences) {
      state.preferences = preferences
    },
    LOAD_STATION_SETTINGS(state, settings) {
      state.settings = settings
    },
    SET_STAGEHAND_HEALTH(state, { stationName, health }) {
      state.stagehandHealth = { ...state.stagehandHealth, [stationName]: health }
    },
    LOAD_GROUPS(state, list) {
      state.groups = list
    },
    LOAD_GROUP_MEMBERSHIPS(state, list) {
      state.groupMemberships = list
    },
    LOAD_NOTIFICATION_ENABLED_STATIONS(state, list) {
      state.notificationEnabledStationsListData = list
    },
    LOAD_ANNOUNCEMENTS(state, records) {
      state.announcements = records
    },
    LOAD_ANNOUNCEMENTS_ARCHIVE(state, records) {
      state.announcementsArchive = records
    }
  },
  actions: {
    async loadStations({ commit, dispatch }) {
      try {
        const response = await api.newsdesk.getStations()
        commit('LOAD_STATIONS', response?.data ?? [])
      } catch (error) {
        throw new Error(`API.newsdesk.getStations() ${error}`)
      }
    },
    async loadStationConfigurations({ commit, dispatch }) {
      const response = await api.newsdesk.getStationConfigurations()
      commit('LOAD_CONFIGS', response?.data ?? [])
    },
    async loadAllStagehandHealth({ commit }) {
      const containers = (await api.docker.getAllStagehandHealth()).data ?? []
      for (const container of containers) {
        if (container.station_name) {
          commit('SET_STAGEHAND_HEALTH', { stationName: container.station_name, health: container.health ?? null })
        }
      }
    },
    async pollStagehandHealth({ commit, state }, stationName) {
      if (!stationName) return
      const station = state.list.find((s) => s.station_name === stationName)
      if (!station?.settings?.node_port) return
      try {
        const info = (await api.nrcs.getStagehandInfo(stationName)).data ?? {}
        commit('SET_STAGEHAND_HEALTH', {
          stationName,
          health: {
            status: 'running',
            version: info.version ?? null,
            uptime: info.uptime ?? null,
            hostname: info.hostname ?? null,
            port: info.port ?? null,
            timezone: info.timezone ?? null,
            error: null
          }
        })
      } catch (e) {
        commit('SET_STAGEHAND_HEALTH', {
          stationName,
          health: {
            status: 'unreachable',
            version: null,
            uptime: null,
            hostname: null,
            port: null,
            timezone: null,
            error: e?.message ?? 'Connection failed'
          }
        })
      }
    },
    async loadSettings({ commit }) {
      try {
        const response = await api.newsdesk.getStationSettings()
        commit('LOAD_STATION_SETTINGS', response?.data ?? {})
      } catch (error) {
        console.error('Could not load station settings', error)
      }
    },
    async setStationByName({ state, dispatch, commit, rootGetters }, name) {
      if (!name) throw new Error('Station name is required')
      // First check if the station exists in our full list
      let station = state.list?.find((data) => data.station_name == name.toLowerCase())
      if (!station) {
        throw new Error(`Station ${name} not found`)
      }
      if (station.active != 1) {
        throw new Error(`Station ${name} is deactivated`)
      }

      // Check if user has access to this station
      const access = rootGetters.access
      const userStations = [...(access?.user ?? []), ...(access?.stagehand ?? []), ...(access?.viewer ?? [])]
      const hasAccess = userStations.some((s) => s.toLowerCase() === name.toLowerCase())

      if (!hasAccess) {
        throw new Error(`No access to station ${name}`)
      }

      // Clear existing data
      commit('LOAD_STORIES', [])
      commit('LOAD_RUNDOWN_ITEMS', [])
      commit('LOAD_ASSETS', [])
      commit('LOAD_ARCHIVES', [])
      commit('LOAD_MY_TASKS', [])
      commit('LOAD_MONITORED_STORIES', [])
      commit('LOAD_CALENDAR_STORIES', [])
      commit('CLEAR_ALL_OPTIONS')
      commit('SET_LAST_UPDATED_AT', 0)
      commit('SET_MEDIA_LAST_UPDATED_AT', 0)
      commit('LOAD_STAGEHAND_INFO', {})

      await dispatch('setStation', station)
    },
    async setDefaultStation({ state, getters, dispatch }, list) {
      let station = {}
      list = list ?? getters.stationList

      if (!list) return console.error('No stations found')

      // set default station
      if (state.current.station && list.length > 0) {
        station = list.find((data) => data.station_name == state.current.station_name)
      }
      // try from cookies
      else if (cookies.get('currentStation') && list.length > 0) {
        station = list.find((data) => data.station_name == cookies.get('currentStation')) ?? station
      }

      // we dont have a current station, select the first station in the list
      if (!station?.station_name && list.length > 0) {
        station = list[0]
      }

      return dispatch('setStation', station)
    },
    async setStation({ commit, state, dispatch }, station) {
      commit('SET_STATION', station)
      if (!station.station_name) return
      cookies.set('currentStation', station.station_name, { expires: 365, secure: true, sameSite: 'strict' })

      // Update feature flag targeting context with new station
      OpenFeature.setContext({ ...OpenFeature.getContext(), station: station.station_name })

      // if we are switching stations, update the url query param
      if (station.station_name !== router.currentRoute.value.query.station) {
        let query = { ...router.currentRoute.value.query, station: station.station_name }
        router.push({ query }).catch(console.debug)
      }

      dispatch('loadStationPreferences').catch(console.error)
      dispatch('loadStagehandInfo').catch(console.error)

      return true
    },
    async loadStationPreferences({ commit, dispatch }) {
      // commit('SET_STATION_PREFERENCES', [])
      let resp = await api.preferences.getStationPreferences()

      if (resp?.data) commit('SET_STATION_PREFERENCES', resp.data)

      // Notify view presets system that station preferences are loaded
      dispatch('notifyStationPreferencesLoaded', {}, { root: true })
    },
    async saveStationPreferences({ dispatch }, payload) {
      // workaround for bug https://gitlab.scrippsnet.app/biztech/newsdesk/-/issues/772
      if (Array.isArray(payload.data) && payload.data.length == 1) payload.data.push({})
      let resp = await api.preferences.updateStationPreferences(payload.key, payload.data)
      await dispatch('loadStationPreferences')
    },
    async createStationPreferences({ dispatch }, payload) {
      let resp = await api.preferences.createStationPreferences(payload)
      await dispatch('loadStationPreferences')
    },
    async deleteStationPreferences({ dispatch }, key) {
      let resp = await api.preferences.deleteStationPreferences(key)
      await dispatch('loadStationPreferences')
    },
    async loadGroups({ commit, dispatch }) {
      try {
        // const gResp = await api.newsdesk.getAllGroups()
        // get the workspace groups from the access store and pluck the id/name from the stations
        let workspaceGroups =
          vuex.getters.access?.workspaceGroups?.map((g) => {
            return {
              ...g,
              stations: g.stations?.map((s) => {
                return { id: s.id, name: s.station_name, location: s.station_location }
              }),
              roles: g.roles?.map((r) => r.okta_role)
            }
          }) ?? []

        // if user has direct access to stations, get the dashboards they should have access to
        if (vuex.getters.access?.stations) {
          try {
            let groups = (await getStationGroups(vuex.getters.access.stations)) ?? []
            // add to workspace groups
            workspaceGroups = workspaceGroups.concat(groups)
          } catch (error) {}
        }
        // make workspaceGroups unique by id
        workspaceGroups = _.uniqBy(workspaceGroups, 'id')

        commit('LOAD_GROUPS', workspaceGroups)
      } catch (error) {
        throw new Error(`API.newsdesk.loadGroups() ${error}`)
      }
    },
    async loadNotificationEnabledStations({ commit, dispatch }) {
      try {
        const response = await api.newsdesk.getApiNotificationEnabledStations()
        commit('LOAD_NOTIFICATION_ENABLED_STATIONS', response?.data ?? [])
      } catch (error) {
        console.warn('Error loadNotificationEnabledStations: ', error)
      }
    },
    async setNotificationEnabledStationsList({ commit }, data) {
      try {
        commit('LOAD_NOTIFICATION_ENABLED_STATIONS', data)
      } catch (error) {
        throw new Error(`API.newsdesk.setNotificationEnabledStations() ${error}`)
      }
    },
    async loadAnnouncements({ commit, state, dispatch }, skipCache = false) {
      const _s = await api.newsdesk.getAnnouncementsListData(skipCache)
      const _s2 = await api.newsdesk.getAnnouncementsArchiveListData(skipCache)
      const announcements = _s?.data ?? []
      const announcementsArchive = _s2?.data ?? []
      commit('LOAD_ANNOUNCEMENTS', announcements)
      commit('LOAD_ANNOUNCEMENTS_ARCHIVE', announcementsArchive)
    }
  },
  getters: {
    currentContainer(state) {
      const stationName = state.current.station_name
      return { ...state.current, health: state.stagehandHealth[stationName] ?? null }
    },
    isStagehandRunning(state) {
      return state.stagehandHealth[state.current.station_name]?.status === 'running'
    },
    stagehandHealth(state) {
      return state.stagehandHealth
    },
    currentStation(state) {
      return state.current
    },
    // get current timezone from the current station fall back to user's timezone
    currentTimezone(state) {
      return state.current?.settings?.tz ?? dayjs.tz.guess()
    },
    stationPreferences(state) {
      return state.preferences
    },
    stationSettings(state) {
      return state.current.stagehand ?? {}
    },
    stationContainers(state) {
      return (state.list ?? []).map((s) => ({ ...s, health: state.stagehandHealth[s.station_name] ?? null }))
    },
    stationConfigs(state) {
      return state.list ?? []
    },
    stationList(state) {
      const allStations = state.list ?? []
      return allStations.filter((s) => s.active == 1)
    },
    workspaceGroups(state) {
      return state.groups
    },
    hasEnterpriseFeature(state, getters, rootState) {
      return true
    },
    isStandAlone(state, getters, rootState) {
      return getters.currentStation.settings?.backend == 'newsdesk' ? true : false
    },
    notificationEnabledStationsList(state) {
      return state.notificationEnabledStationsListData
    },
    announcements(state) {
      return state.announcements
    },
    announcementsArchive(state) {
      return state.announcementsArchive
    }
  }
}

const getStationGroups = async (stations) => {
  // get the group memberships for the stations the user has access to
  const mResp = await api.newsdesk.getGroupsByStations(vuex.getters.access.stations)
  const stationsWithGroups = mResp?.data?.results ?? []

  // extract all the groups
  let groups = []
  stationsWithGroups.forEach((s) => {
    groups = groups.concat(s.groups)
  })

  // remove the dupes
  groups = _.uniqBy(groups, 'id')

  // add the station attribute to each group
  groups.forEach((g) => {
    g.stations = stationsWithGroups
      .filter((s) => {
        return s.groups.some((sg) => sg.id == g.id)
      })
      .map((s) => ({ id: s.id, name: s.station_name }))
  })
  return groups
}
